<template>
  <!-- DESIGN 1: PREMIUM CARD WITH GRADIENT ACCENT -->
  <div class="notification-container">
    <v-card
      v-if="designType === 'premium'"
      class="premium-notification"
      :class="{
        'notification-unread': !notification.read,
        'blast-animation': showBlastEffect
      }"
      elevation="3"
      @click="handleClick"
      v-ripple
    >
      <!-- Blast Effect Elements -->
      <div v-if="showBlastEffect" class="blast-particles">
        <div v-for="n in 8" :key="`particle-${n}`" class="blast-particle"></div>
      </div>

      <!-- Accent Color Stripe -->
      <div class="notification-accent"></div>

      <div class="notification-inner">
        <!-- Priority Badge (optional) -->
        <div
          v-if="notification.priority"
          class="priority-badge"
          :class="`priority-${notification.priority}`"
        >
          {{ notification.priority }}
        </div>

        <!-- Content Area -->
        <div class="notification-content">
          <div class="d-flex">
            <!-- Avatar/Image -->
            <div v-if="notification.image" class="notification-avatar-wrapper">
              <v-avatar class="notification-avatar">
                <v-img :src="notification.image" />
              </v-avatar>
            </div>
            <div v-else class="notification-avatar-wrapper">
              <v-avatar class="notification-avatar" color="primary">
                <v-icon color="white">{{ getNotificationIcon() }}</v-icon>
              </v-avatar>
            </div>

            <!-- Main Content -->
            <div class="notification-main">
              <div v-if="notification.title" class="notification-title">
                {{ notification.title }}
              </div>
              <div
                class="notification-message"
                v-html="notification.text"
              ></div>
              <div class="notification-meta">
                <!-- <v-chip x-small outlined class="category-chip">
                  {{ notification.category || 'System' }}
                </v-chip> -->
                <span class="notification-time">{{
                  getTimeAgo(notification.date)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Area -->
        <div class="notification-actions">
          <v-btn icon small @click.stop="markAsReadToggle" class="action-btn">
            <v-icon size="20">{{
              notification.read
                ? "mdi-email-open-outline"
                : "mdi-email-mark-as-unread"
            }}</v-icon>
          </v-btn>
          <v-menu offset-y bottom left transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click.stop
                class="action-btn"
              >
                <v-icon size="20">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(item, i) in dropMenus"
                :key="i"
                @click="onSelectedMenu(item)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon size="18">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-card>

    <!-- DESIGN 2: MODERN GLASS MORPHISM STYLE -->
    <v-card
      v-else-if="designType === 'glassmorphic'"
      class="glass-notification"
      :class="{
        'notification-unread': !notification.read,
        'glass-blast-animation': showBlastEffect
      }"
      @click="handleClick"
      v-ripple
    >
      <!-- Glass Blast Ripple Effect -->
      <div v-if="showBlastEffect" class="ripple-container">
        <div class="ripple-effect"></div>
      </div>

      <div class="glass-inner">
        <!-- Status Indicator -->
        <div
          class="status-indicator"
          :class="{ 'status-unread': !notification.read }"
        ></div>

        <!-- Avatar Area -->
        <div class="glass-avatar-area">
          <v-avatar size="48" class="glass-avatar">
            <v-img v-if="notification.image" :src="notification.image"></v-img>
            <template v-else>
              <div class="avatar-fallback">
                <v-icon size="24">{{ getNotificationIcon() }}</v-icon>
              </div>
            </template>
          </v-avatar>
        </div>

        <!-- Content Area -->
        <div class="glass-content">
          <div class="glass-title">
            {{ notification.title || "Notification" }}
            <span v-if="!notification.read" class="unread-dot"></span>
          </div>
          <div class="glass-message" v-html="notification.text"></div>
          <div class="glass-meta">
            <v-icon size="14" class="time-icon">mdi-clock-outline</v-icon>
            <span>{{ getTimeAgo(notification.date) }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="glass-actions">
          <v-btn
            icon
            x-small
            class="glass-action-btn"
            @click.stop="markAsReadToggle"
          >
            <v-icon>{{
              notification.read ? "mdi-email-open" : "mdi-email"
            }}</v-icon>
          </v-btn>
          <v-menu offset-y bottom left transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                class="glass-action-btn"
                v-bind="attrs"
                v-on="on"
                @click.stop
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense class="glass-menu">
              <v-list-item
                v-for="(item, i) in dropMenus"
                :key="i"
                @click="onSelectedMenu(item)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon size="18">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-card>

    <!-- DESIGN 3: ENTERPRISE DASHBOARD STYLE -->
    <v-card
      v-else-if="designType === 'enterprise'"
      class="enterprise-notification"
      :class="{
        'notification-unread': !notification.read,
        'enterprise-blast-animation': showBlastEffect
      }"
      @click="handleClick"
      v-ripple
    >
      <!-- Enterprise Blast Effect -->
      <div v-if="showBlastEffect" class="enterprise-blast-container">
        <div class="enterprise-blast-ring"></div>
        <div class="enterprise-blast-highlight"></div>
      </div>

      <!-- Status bar -->
      <div class="status-bar" :class="getStatusClass()"></div>

      <div class="enterprise-inner">
        <!-- Left Section: Avatar & Category -->
        <div class="enterprise-left">
          <div class="enterprise-avatar-container">
            <v-avatar size="42" class="enterprise-avatar">
              <v-img v-if="notification.image" :src="notification.image" />
              <template v-else>
                <div
                  class="enterprise-avatar-fallback"
                  :class="getStatusClass()"
                >
                  <v-icon size="20" color="white">{{
                    getNotificationIcon()
                  }}</v-icon>
                </div>
              </template>
            </v-avatar>
          </div>
          <!-- <div class="enterprise-category">
            {{ notification.category || 'System' }}
          </div> -->
        </div>

        <!-- Center Section: Content -->
        <div class="enterprise-content">
          <div class="enterprise-header">
            <div class="enterprise-title">
              {{ notification.title || "Notification" }}
            </div>
            <div class="enterprise-time">
              {{ getTimeAgo(notification.date) }}
            </div>
          </div>
          <div class="enterprise-message" v-html="notification.text"></div>
        </div>

        <!-- Right Section: Actions -->
        <div class="enterprise-actions">
          <div class="enterprise-action-buttons">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="markAsReadToggle"
                  class="enterprise-btn"
                >
                  <v-icon size="18">{{
                    notification.read ? "mdi-email-open" : "mdi-email"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>{{
                notification.read ? "Mark as unread" : "Mark as read"
              }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="deleteNotif"
                  class="enterprise-btn"
                >
                  <v-icon size="18">mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>

            <v-menu offset-y bottom left transition="slide-y-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                  class="enterprise-btn"
                >
                  <v-icon size="18">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(item, i) in additionalActions"
                  :key="i"
                  @click="onSelectedMenu(item)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon size="18">{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  props: {
    notification: {
      type: Object,
      required: true
    },
    designType: {
      type: String,
      default: "premium",
      validator: value =>
        ["premium", "glassmorphic", "enterprise"].includes(value)
    }
  },

  data() {
    return {
      dropMenus: [],
      additionalActions: [
        { title: "Archive", icon: "mdi-archive-outline" },
        { title: "Forward", icon: "mdi-forward" },
        { title: "Snooze", icon: "mdi-clock-outline" }
      ],
      showBlastEffect: false
    };
  },

  methods: {
    ...mapActions("notification", {
      markAsRead: "markAsRead",
      deleteNotification: "deleteNotification"
    }),

    handleClick() {
      // Show blast effect
      this.triggerBlastAnimation();

      // After a small delay, proceed with normal click action
      setTimeout(() => {
        this.goToDetails();
      }, 300);
    },

    triggerBlastAnimation() {
      this.showBlastEffect = true;

      // Reset animation after it completes
      setTimeout(() => {
        this.showBlastEffect = false;
      }, 1000);
    },

    goToDetails() {
      if (!this.notification.read) {
        // Only show read animation for unread notifications
        this.$emit("notification-read-animation", this.notification._id);
      }

      this.markAsRead({ _id: this.notification._id });
      if (
        this.notification.url &&
        this.notification.url.startsWith("https://")
      ) {
        window.open(this.notification.url, "_blank");
      } else if (this.notification.url) {
        this.$router.push(this.notification.url);
      }
    },

    getTimeAgo(oldTimeStr) {
      const oldTime = Date.parse(oldTimeStr);
      const time = new Date();
      const different = (time.getTime() - oldTime) / 1000;
      const month = Math.floor(different / 3600 / 24 / 30);
      const days = Math.floor((different / 3600 / 24) % 30);
      const hours = Math.floor((different / 3600) % 24);
      const mins = Math.floor((different / 60) % 60);

      if (month === 1) return `1 month ago`;
      if (month > 1) return `${month} months ago`;
      if (days === 1) return `Yesterday`;
      if (days > 1) return `${days} days ago`;
      if (hours === 1) return `1 hour ago`;
      if (hours > 1) return `${hours} hours ago`;
      if (mins === 1) return `1 minute ago`;
      if (mins > 1) return `${mins} minutes ago`;
      return `Just now`;
    },

    getNotificationIcon() {
      const typeIcons = {
        info: "mdi-information-outline",
        alert: "mdi-alert-circle-outline",
        warning: "mdi-alert-outline",
        success: "mdi-check-circle-outline",
        error: "mdi-alert-circle",
        message: "mdi-message-text-outline",
        update: "mdi-update",
        calendar: "mdi-calendar",
        task: "mdi-playlist-check"
      };

      return typeIcons[this.notification.type] || "mdi-bell-outline";
    },

    getStatusClass() {
      const statusClasses = {
        info: "status-info",
        alert: "status-alert",
        warning: "status-warning",
        success: "status-success",
        error: "status-error"
      };

      return statusClasses[this.notification.type] || "status-default";
    },

    onSelectedMenu(item) {
      // Show blast effect on menu item click
      this.triggerBlastAnimation();

      if (item.title === "Delete") {
        this.deleteNotif();
      } else if (
        item.title === "Mark as read" ||
        item.title === "Mark as unread"
      ) {
        this.markAsReadToggle();
      } else {
        // Handle other menu items
        this.$emit("notification-action", {
          type: item.title.toLowerCase(),
          id: this.notification.id
        });
      }
    },

    markAsReadToggle() {
      // Show blast effect when toggling read status
      this.triggerBlastAnimation();

      if (this.notification.read) {
        // Implement mark as unread functionality if available
        this.$emit("notification-unread", this.notification._id);
      } else {
        this.markAsRead({ _id: this.notification._id })
          .then(() => {
            this.$emit("notification-read", this.notification.id);
          })
          .catch(error => {
            console.error("Failed to mark notification as read:", error);
          });
      }
    },

    deleteNotif() {
      // Show blast effect when deleting
      this.triggerBlastAnimation();

      this.deleteNotification({ _id: this.notification._id })
        .then(() => {
          this.$emit("notification-deleted", this.notification.id);
        })
        .catch(error => {
          console.error("Failed to delete notification:", error);
        });
    },

    updateMenuOptions() {
      if (!this.notification.read) {
        this.dropMenus = [
          { title: "Mark as read", icon: "mdi-email-open-outline" },
          { title: "Delete", icon: "mdi-delete-outline" }
        ];
      } else {
        this.dropMenus = [
          // { title: "Mark as unread", icon: "mdi-email-mark-as-unread" },
          { title: "Delete", icon: "mdi-delete-outline" }
        ];
      }
    }
  },

  mounted() {
    this.updateMenuOptions();
  },

  watch: {
    "notification.read": {
      handler() {
        this.updateMenuOptions();
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
/* SHARED STYLES */
.notification-container {
  width: 100%;
  font-family: "Roboto", sans-serif;
}

/* ========== PREMIUM NOTIFICATION STYLES ========== */
.premium-notification {
  display: flex;
  margin-bottom: 12px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  cursor: pointer;
  background: #fff;
}

.premium-notification:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.notification-unread .notification-accent {
  opacity: 1;
}

.notification-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  background: linear-gradient(to bottom, #0a5b8a, #0288d1);
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.notification-inner {
  display: flex;
  width: 100%;
  padding: 16px;
  position: relative;
}

.priority-badge {
  position: absolute;
  top: 12px;
  right: 16px;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
}

.priority-high {
  background-color: #f44336;
}

.priority-medium {
  background-color: #ff9800;
}

.priority-low {
  background-color: #4caf50;
}

.notification-content {
  flex-grow: 1;
  min-width: 0;
}

.notification-avatar-wrapper {
  margin-right: 16px;
}

.notification-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid white;
}

.notification-main {
  flex-grow: 1;
  min-width: 0;
}

.notification-title {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.87);
}

.notification-message {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 8px;
  line-height: 1.4;
  word-break: break-word;
}

.notification-meta {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
}

.category-chip {
  margin-right: 8px;
  height: 20px !important;
}

.notification-time {
  color: rgba(0, 0, 0, 0.54);
}

.notification-actions {
  display: flex;
  align-items: flex-start;
  margin-left: 16px;
}

/* Premium Blast Animation */
.blast-animation {
  animation: pulse-glow 0.6s ease-in-out;
}

.blast-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.blast-particle {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(to bottom right, #2196f3, #0d47a1);
  opacity: 0;
  transform: scale(0);
}

.blast-particle:nth-child(1) {
  top: 20%;
  left: 10%;
  animation: blast-particle 0.8s ease-out;
}

.blast-particle:nth-child(2) {
  top: 30%;
  left: 80%;
  animation: blast-particle 0.7s ease-out 0.1s;
}

.blast-particle:nth-child(3) {
  top: 70%;
  left: 20%;
  animation: blast-particle 0.9s ease-out 0.05s;
}

.blast-particle:nth-child(4) {
  top: 50%;
  left: 90%;
  animation: blast-particle 0.8s ease-out 0.15s;
}

.blast-particle:nth-child(5) {
  top: 80%;
  left: 50%;
  animation: blast-particle 0.7s ease-out 0.2s;
}

.blast-particle:nth-child(6) {
  top: 10%;
  left: 60%;
  animation: blast-particle 0.9s ease-out 0.1s;
}

.blast-particle:nth-child(7) {
  top: 40%;
  left: 30%;
  animation: blast-particle 0.8s ease-out 0.15s;
}

.blast-particle:nth-child(8) {
  top: 60%;
  left: 70%;
  animation: blast-particle 0.75s ease-out 0.05s;
}

.action-btn {
  position: relative;
  overflow: hidden;
}

.action-btn::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: rgba(33, 150, 243, 0.3);
  opacity: 0;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(1);
  transition: all 0.4s ease-out;
}

.action-btn:active::after {
  opacity: 1;
  transform: translate(-50%, -50%) scale(20);
}

/* ========== GLASSMORPHIC NOTIFICATION STYLES ========== */
.glass-notification {
  margin-bottom: 12px;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 24px rgba(31, 38, 135, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.glass-notification:hover {
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.2);
  transform: translateY(-2px);
}

.glass-inner {
  display: flex;
  align-items: center;
  padding: 16px;
  position: relative;
}

.status-indicator {
  width: 4px;
  height: 70%;
  position: absolute;
  left: 0;
  top: 15%;
  background: #e0e0e0;
  border-radius: 0 4px 4px 0;
  transition: background 0.3s ease;
}

.status-unread {
  background: linear-gradient(to bottom, #3f51b5, #2196f3);
}

.glass-avatar-area {
  margin-right: 16px;
  flex-shrink: 0;
}

.glass-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.7);
  background: linear-gradient(145deg, #e6e6e6, #ffffff);
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(145deg, #0288d1, #0a5b8a);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.glass-content {
  flex-grow: 1;
  min-width: 0;
  margin-right: 16px;
}

.glass-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.unread-dot {
  width: 8px;
  height: 8px;
  background: #2196f3;
  border-radius: 50%;
  margin-left: 8px;
  display: inline-block;
}

.glass-message {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 6px;
  line-height: 1.4;
  word-break: break-word;
}

.glass-meta {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
}

.time-icon {
  margin-right: 4px;
}

.glass-actions {
  display: flex;
  align-items: center;
}

.glass-action-btn {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-left: 8px;
  transition: all 0.2s ease;
}

.glass-action-btn:hover {
  background: rgba(255, 255, 255, 0.8);
}

.glass-menu {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Glass Blast Animation */
.glass-blast-animation {
  animation: glass-flash 0.6s ease;
}

.ripple-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

.ripple-effect {
  position: absolute;
  border-radius: 50%;
  width: 10px;
  height: 10px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(33, 150, 243, 0.2) 70%,
    transparent 100%
  );
  opacity: 0;
  animation: ripple 0.8s ease-out;
}

/* ========== ENTERPRISE NOTIFICATION STYLES ========== */
.enterprise-notification {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.enterprise-notification:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
}

.enterprise-notification.notification-unread {
  background-color: rgba(33, 150, 243, 0.03);
}

.status-bar {
  height: 4px;
  width: 100%;
  background-color: #e0e0e0;
}

.status-info {
  background-color: #2196f3;
}

.status-alert {
  background-color: #ff9800;
}

.status-warning {
  background-color: #ff5722;
}

.status-success {
  background-color: #4caf50;
}

.status-error {
  background-color: #f44336;
}

.status-default {
  background-color: #9e9e9e;
}

.enterprise-inner {
  display: flex;
  padding: 16px;
  align-items: flex-start;
}

.enterprise-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 16px;
  flex-shrink: 0;
  width: 60px;
}

.enterprise-avatar-container {
  margin-bottom: 8px;
}

.enterprise-avatar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.enterprise-avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #9e9e9e;
}

.enterprise-category {
  font-size: 0.65rem;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.enterprise-content {
  flex-grow: 1;
  min-width: 0;
  margin-right: 16px;
}

.enterprise-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.enterprise-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
}

.enterprise-time {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
}

.enterprise-message {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.4;
  word-break: break-word;
}

.enterprise-actions {
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
}

.enterprise-action-buttons {
  display: flex;
}

.enterprise-action-buttons .v-btn {
  margin-left: 4px;
}

/* Enterprise Blast Animation */
.enterprise-blast-animation {
  animation: enterprise-pulse 0.5s ease;
}

.enterprise-blast-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}

.enterprise-blast-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #2196f3;
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  animation: ring-expand 0.6s ease-out;
}

.enterprise-blast-highlight {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    circle at center,
    rgba(33, 150, 243, 0.2) 0%,
    transparent 70%
  );
  opacity: 0;
  animation: highlight-fade 0.8s ease-out;
}

.enterprise-btn {
  position: relative;
  overflow: hidden;
}

.enterprise-btn::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: rgba(33, 150, 243, 0.3);
  opacity: 0;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(1);
  transition: all 0.3s ease-out;
}

.enterprise-btn:active::after {
  opacity: 1;
  transform: translate(-50%, -50%) scale(20);
}

/* Animation Keyframes */
@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 rgba(33, 150, 243, 0);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 20px rgba(33, 150, 243, 0.6);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 rgba(33, 150, 243, 0);
    transform: scale(1);
  }
}

@keyframes blast-particle {
  0% {
    transform: scale(0);
    opacity: 0.8;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
}

@keyframes glass-flash {
  0% {
    box-shadow: 0 4px 24px rgba(31, 38, 135, 0.1);
    backdrop-filter: blur(10px);
  }
  50% {
    box-shadow: 0 0 30px rgba(33, 150, 243, 0.6);
    backdrop-filter: blur(15px);
  }
  100% {
    box-shadow: 0 4px 24px rgba(31, 38, 135, 0.1);
    backdrop-filter: blur(10px);
  }
}

@keyframes ripple {
  0% {
    width: 0;
    height: 0;
    opacity: 0.8;
  }
  100% {
    width: 500px;
    height: 500px;
    opacity: 0;
  }
}

@keyframes enterprise-pulse {
  0% {
    background-color: inherit;
  }
  30% {
    background-color: rgba(33, 150, 243, 0.1);
  }
  100% {
    background-color: inherit;
  }
}

@keyframes ring-expand {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.8;
    border-width: 20px;
  }
  100% {
    transform: translate(-50%, -50%) scale(7);
    opacity: 0;
    border-width: 1px;
  }
}

@keyframes highlight-fade {
  0% {
    opacity: 0;
  }
  30% {
    opacity: 0.5;
  }
  100% {
    opacity: 0;
  }
}

/* Read Animation Styles */
.read-wave-animation {
  position: relative;
}

.read-wave-animation::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.7),
    transparent
  );
  transform: translateX(-100%);
  animation: read-wave 1s ease-in-out;
  z-index: 2;
  pointer-events: none;
}

@keyframes read-wave {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Media Queries for Responsive Design */
@media (max-width: 600px) {
  .notification-inner,
  .glass-inner,
  .enterprise-inner {
    flex-direction: column;
  }

  .notification-actions,
  .glass-actions,
  .enterprise-actions {
    margin-top: 12px;
    margin-left: 0;
    align-self: flex-end;
  }

  .enterprise-left {
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    margin-bottom: 12px;
  }

  .enterprise-avatar-container {
    margin-right: 12px;
    margin-bottom: 0;
  }

  .enterprise-category {
    text-align: left;
  }
}
</style>
